import {
  Container,
  Box,
  TextField,
  Typography,
  Button,
  Grid2,
  InputLabel,
  Paper,
  Select,
  MenuItem,
  Divider,
  FormHelperText,
  Alert,
  Snackbar,
  AlertTitle
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { find, includes, isEmpty, isNil, keys, map } from 'lodash/fp'
import {
  Control,
  Controller,
  FieldErrors,
  FormProvider,
  useForm,
  UseFormSetValue
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
// import { format } from 'date-fns/fp'
import { AppEntrySubscription } from '@mzc-watchover/terashite-entry-graphql'

import { RegistrationStatus } from '../../types'
import incompleteConfig from '../../assets/incompleteConfig.json'
import subscriptionStatusConfig from '../../assets/subscriptionStatusConfig.json'
import contractStatusConfig from '../../assets/contractStatusConfig.json'
import entryPatternConfig from '../../assets/entryPatternConfig.json'
import paymentTypeConfig from '../../assets/paymentTypeConfig.json'
import { getCustomerInfo, updateMember } from '../../functions/common'
import { Loading } from '../Loading'
import { ServerError } from '../ServerError'
import { Entry } from '../../../../cdk/terashiteEntry/lib/container-api/src/types'
import { format, parseISO } from 'date-fns/fp'

const setSubscriptionValues = (
  data: AppEntrySubscription,
  setValue: UseFormSetValue<any>
) => {
  map((key: string) => {
    if (
      includes(key)([
        'cancellationDate',
        'scheduledAbolitionDate',
        'applicationDate',
        'contractDate'
      ])
    ) {
      setValue(
        key,
        !isNil(data[key as keyof typeof data]) &&
          !isEmpty(data[key as keyof typeof data])
          ? format('yyyy-MM-dd')(
              parseISO(data[key as keyof typeof data] as string)
            )
          : ''
      )
    } else {
      setValue(
        key,
        !isNil(data[key as keyof typeof data])
          ? data[key as keyof typeof data]
          : ''
      )
    }
  })(keys(data))
}
const setPaymentPlans = (data: string[], setValue: UseFormSetValue<any>) => {
  map((key: string) => {
    setValue(
      `paymentPlan${key}`,
      !isNil(data[key as keyof typeof data])
        ? data[key as keyof typeof data]
        : ''
    )
  })(keys(data))
}
const setEntryValues = (data: Entry, setValue: UseFormSetValue<any>) => {
  map((key: string) => {
    setValue(
      key,
      !isNil(data[key as keyof typeof data])
        ? data[key as keyof typeof data]
        : ''
    )
  })(keys(data))
}
const CommonSelect = (props: {
  id: string
  master: Record<string, string>
  control: Control<any, any>
  errors: FieldErrors<any>
  defaultCd?: string
  disabled?: boolean
  allowEmpty?: boolean
}) => {
  const { id, master, defaultCd, disabled, control, errors, allowEmpty } = props
  if (isEmpty(master)) return <></>
  return (
    <Controller
      name={id}
      control={control}
      render={({ field }) => {
        return (
          <>
            <Select
              {...field}
              id={id}
              required
              defaultValue={!isNil(defaultCd) ? defaultCd : 'none'}
              variant="standard"
              disabled={!isNil(disabled) ? disabled : false}
              sx={{ minWidth: '50%', textAlign: 'center' }}>
              {!isNil(allowEmpty) && allowEmpty ? (
                <MenuItem value=""></MenuItem>
              ) : (
                <></>
              )}
              {map((key: string) => (
                <MenuItem value={key}>
                  {master[key as keyof typeof master]}
                </MenuItem>
              ))(keys(master))}
            </Select>
            <FormHelperText>{errors[id]?.message as string}</FormHelperText>
          </>
        )
      }}
    />
  )
}

export const ContractUpdate = () => {
  const navigate = useNavigate()
  const { memberId } = useParams<{ memberId: string }>()
  const [kind, setKind] = useState<RegistrationStatus | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [alertStatus, setAlertStatus] = useState<boolean>(false)
  const entrySchema = {
    memberId: z.string(),
    statusCd: z.string(),
    tcId: z.string(),
    lastName: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    firstName: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    lastNameKana: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[ぁ-ゞー]+$/, 'ひらがなで入力してください'),
    firstNameKana: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[ぁ-ゞー]+$/, 'ひらがなで入力してください'),
    postalCode: z.string(),
    pref: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    city: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    town: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    place: z
      .string()
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください')
      .or(z.string().length(0)),
    phone: z
      .string()
      .min(12, { message: '数字は10桁または11桁で入力してください' })
      .max(13, { message: '数字は10桁または11桁で入力してください' })
      .regex(/^[0-9,-]+$/, '半角数字または半角ハイフンで入力してください'),
    customerNumber: z.string(),
    payment: z.string().min(1, { message: '選択してください' }),
    paymentNumber: z.string().optional(),
    contractorName: z.string().optional(),
    contractorNameKana: z.string().optional(),
    contractorPostalCode: z.string().optional(),
    contractorPhone: z.string().optional(),
    couponCode: z.string().optional(),
    email: z
      .string()
      .email({ message: '有効なメールアドレスを入力してください' }),
    incompleteCd: z.string().optional(),
    incompleteDetail: z.string().optional(),
    pattern: z.string().optional(),
    notes: z.string().nullable().optional()
  }
  const subscriptionSchema = {
    memberId: z.string().nullable().optional(),
    tcid: z.string().nullable().optional(),
    statusCd: z.string().nullable().optional(),
    lastName: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    firstName: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    lastNameHiragana: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[ぁ-ゞー]+$/, 'ひらがなで入力してください'),
    firstNameHiragana: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[ぁ-ゞー]+$/, 'ひらがなで入力してください'),
    zip: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[0-9]+$/, '9999999のフォーマットで入力してください'),
    state: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    city: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    address: z
      .string()
      .min(1, { message: '必須入力です' })
      .regex(/^[^ -~｡-ﾟ]+$/, '全角で入力してください'),
    phoneNo: z
      .string()
      .min(10, { message: '数字は10桁または11桁で入力してください' })
      .max(11, { message: '数字は10桁または11桁で入力してください' })
      .regex(/^[0-9,-]+$/, '半角数字または半角ハイフンで入力してください'),
    mail: z
      .string()
      .email({ message: '有効なメールアドレスを入力してください' }),
    zuoraAccountId: z.string().nullable().optional(),
    zuoraAccountNo: z.string().nullable().optional(),
    zuoraSubscriptionId: z.string().nullable().optional(),
    kateeneId: z.string().nullable().optional(),
    cancellationDate: z.string().nullable().optional().nullable().optional(),
    deleteFlg: z.boolean(),
    customerId: z.string().nullable().optional(),
    cisName: z.string().nullable().optional(),
    cisNameKana: z.string().nullable().optional(),
    cisZip: z.string().nullable().optional(),
    cisPhoneNo: z.string().nullable().optional(),
    cisAddress: z.string().nullable().optional(),
    cisPaymentTypeCd: z.string().nullable().optional(),
    serviceNo: z.string().nullable().optional(),
    supplyPoint: z.string().nullable().optional(),
    renewableEnergyFlg: z.preprocess(
      (input) => JSON.parse(`${input}`),
      z.boolean()
    ),
    highVoltageFlg: z.preprocess(
      (input) => JSON.parse(`${input}`),
      z.boolean()
    ),
    contractTypeCd: z.string().nullable().optional(),
    masteringCompleteFlg: z.preprocess(
      (input) => JSON.parse(`${input}`),
      z.boolean()
    ),
    planTypeCd: z.string().nullable().optional(),
    demandStartDate: z.string().nullable().optional(),
    contractCapacity: z.string().nullable().optional(),
    contractCapacityCd: z.string().nullable().optional(),
    scheduledAbolitionDate: z.string().nullable().optional(),
    notes: z.string().nullable().optional()
  }
  const [schema, setSchema] = useState<any>(z.object(entrySchema))
  useEffect(() => {
    const getMemberInfo = async () => {
      if (isNil(memberId)) {
        return undefined
      }
      const response = await getCustomerInfo(memberId)
      if (isNil(response)) {
        setHasError(true)
        setLoading(false)
        return undefined
      }
      const {
        status: reciveStatus,
        subscription,
        entry,
        paymentSubscriptions
      } = response
      if (
        reciveStatus === RegistrationStatus.subscription &&
        !isNil(subscription)
      ) {
        setKind(RegistrationStatus.subscription)
        setSubscriptionValues(subscription, setValue)
        setSchema(z.object(subscriptionSchema))
        if (!isNil(paymentSubscriptions) && !isEmpty(paymentSubscriptions)) {
          setPaymentPlans(paymentSubscriptions, setValue)
        }
      } else if (reciveStatus === RegistrationStatus.entry && !isNil(entry)) {
        setKind(RegistrationStatus.entry)
        setEntryValues(entry, setValue)
        setSchema(z.object(entrySchema))
      }
      setLoading(false)
    }
    getMemberInfo()
  }, [])

  const useFormMethods = useForm({
    resolver: zodResolver(schema)
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues
  } = useFormMethods

  const handleClickBack = () => {
    navigate('/admin')
  }
  const handleAlertClose = () => {
    setAlertStatus(false)
  }
  const onSubmit = async (data: any) => {
    setLoading(true)
    const updateEntryOrSubscription = async () => {
      if (isNil(kind)) {
        return
      }
      const response = await updateMember(
        kind,
        kind === RegistrationStatus.entry ? data : undefined,
        kind === RegistrationStatus.subscription ? data : undefined
      )
      if (isNil(response)) {
        return undefined
      }
    }
    updateEntryOrSubscription()
      .then(() => {
        window.location.reload()
      })
      .catch(() => {
        setAlertStatus(true)
      })
  }
  return loading ? (
    <Loading />
  ) : hasError ? (
    <ServerError />
  ) : (
    <>
      <FormProvider {...useFormMethods}>
        <Container sx={{ pt: 1 }}>
          <Box component="form">
            <Typography
              align="left"
              variant="h4"
              sx={{
                borderLeft: 20,
                borderBottom: 1,
                borderColor: 'orange',
                paddingLeft: 1
              }}>
              契約内容更新・詳細
            </Typography>
            <Grid2 container mt={2} spacing={2}>
              <Grid2
                display={'flex'}
                justifyContent={'space-around'}
                alignItems={'center'}
                size={{ xs: 12, lg: 6 }}>
                <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                  会員番号
                </InputLabel>
                <TextField
                  id="memberId"
                  {...register('memberId')}
                  helperText={errors.memberId?.message as string}
                  required
                  variant="standard"
                  disabled
                  margin="dense"
                  sx={{ minWidth: '50%' }}></TextField>
              </Grid2>
              <Grid2
                display={'flex'}
                justifyContent={'space-around'}
                alignItems={'center'}
                size={{ xs: 12, lg: 6 }}>
                <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                  申込ステータス
                </InputLabel>
                <CommonSelect
                  id="statusCd"
                  control={control}
                  errors={errors}
                  {...register('statusCd')}
                  master={
                    kind === RegistrationStatus.entry
                      ? contractStatusConfig
                      : subscriptionStatusConfig
                  }
                  disabled={isNil(
                    find((key: string) => key === getValues('statusCd'))(
                      keys(
                        kind === RegistrationStatus.entry
                          ? contractStatusConfig
                          : subscriptionStatusConfig
                      )
                    )
                  )}
                />
              </Grid2>
            </Grid2>
            {kind === RegistrationStatus.entry ? (
              <>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    申込管理情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込日
                      </InputLabel>
                      <TextField
                        id="applicationDate"
                        // defaultValue={applicationDate}
                        {...register('applicationDate')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込不備内容
                      </InputLabel>
                      <CommonSelect
                        id="incompleteCd"
                        control={control}
                        errors={errors}
                        {...register('incompleteCd')}
                        master={incompleteConfig}
                        allowEmpty={true}
                        // defaultCd={incompleteCd}
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込パターン
                      </InputLabel>
                      <CommonSelect
                        id="pattern"
                        control={control}
                        errors={errors}
                        {...register('pattern')}
                        master={entryPatternConfig}
                        // defaultCd={pattern}
                      />
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込不備内容詳細
                      </InputLabel>
                      <TextField
                        id="incompleteDetail"
                        {...register('incompleteDetail')}
                        multiline
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '50%' }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    申込者情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        氏名（漢字）
                      </InputLabel>
                      <TextField
                        id="lastName"
                        {...register('lastName')}
                        error={'lastName' in errors}
                        helperText={errors.lastName?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '25%',
                          textAlign: 'center'
                        }}></TextField>
                      <TextField
                        id="firstName"
                        {...register('firstName')}
                        error={'firstName' in errors}
                        helperText={errors.firstName?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '25%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        TCIDメールアドレス
                      </InputLabel>
                      <TextField
                        id="email"
                        {...register('email')}
                        error={'email' in errors}
                        helperText={errors.email?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        氏名（かな）
                      </InputLabel>
                      <TextField
                        id="lastNameKana"
                        {...register('lastNameKana')}
                        error={'lastNameKana' in errors}
                        helperText={errors.lastNameKana?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '25%',
                          textAlign: 'center'
                        }}></TextField>
                      <TextField
                        id="firstNameKana"
                        {...register('firstNameKana')}
                        error={'firstNameKana' in errors}
                        helperText={errors.firstNameKana?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '25%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        郵便番号
                      </InputLabel>
                      <TextField
                        id="postalCode"
                        {...register('postalCode')}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        電話番号
                      </InputLabel>
                      <TextField
                        id="phone"
                        {...register('phone')}
                        error={'phone' in errors}
                        helperText={errors.phone?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        住所•建物名等
                      </InputLabel>
                      <TextField
                        id="pref"
                        {...register('pref')}
                        error={'pref' in errors}
                        helperText={errors.pref?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '10%', maxWidth: '15%' }}></TextField>
                      <TextField
                        id="city"
                        {...register('city')}
                        error={'city' in errors}
                        helperText={errors.city?.message as string}
                        multiline
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '10%' }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'end'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}></Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'end'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel
                        sx={{
                          minWidth: '40%',
                          textAlign: 'center'
                        }}></InputLabel>
                      <TextField
                        id="town"
                        {...register('town')}
                        error={'town' in errors}
                        helperText={errors.town?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '50%', mr: 2 }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'end'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}></Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'end'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel
                        sx={{
                          minWidth: '40%',
                          textAlign: 'center'
                        }}></InputLabel>
                      <TextField
                        id="place"
                        {...register('place')}
                        error={'place' in errors}
                        helperText={errors.place?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '50%', mr: 2 }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>

                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    契約プラン情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        紹介コード
                      </InputLabel>
                      <TextField
                        id="couponCode"
                        {...register('couponCode')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    被見守者情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        お客さま番号
                      </InputLabel>
                      <TextField
                        id={
                          kind === RegistrationStatus.entry
                            ? 'customerNumber'
                            : 'customerId'
                        }
                        {...register(
                          kind === RegistrationStatus.entry
                            ? 'customerNumber'
                            : 'customerId'
                        )}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    {/* <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        お客さま番号入力状況
                      </InputLabel>
                      <CommonSelect
                        id="customerStatus"
                        control={control}
                        errors={errors}
                        master={customuerStatusConfig}
                        // defaultCd={'1'}
                      />
                    </Grid2> */}
                  </Grid2>
                  <Grid2 container mt={2} size={{ xs: 12, lg: 6 }}>
                    <Grid2
                      container
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <Typography
                        bgcolor={'#EEE'}
                        variant="subtitle1"
                        align="center"
                        sx={{ width: '100%' }}>
                        お客さま入力情報
                      </Typography>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約名義（漢字）
                        </InputLabel>
                        <TextField
                          id="contractorName"
                          {...register('contractorName')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約名義（かな）
                        </InputLabel>
                        <TextField
                          id="contractorNameKana"
                          {...register('contractorNameKana')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          需要場所郵便番号
                        </InputLabel>
                        <TextField
                          id="contractorPostalCode"
                          {...register('contractorPostalCode')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約電話番号
                        </InputLabel>
                        <TextField
                          id="contractorPhone"
                          {...register('contractorPhone')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                    </Grid2>
                    <Grid2
                      container
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <Typography
                        bgcolor={'#EEE'}
                        variant="subtitle1"
                        align="center"
                        sx={{ width: '100%' }}>
                        CIS登録情報
                      </Typography>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約名義（漢字）
                        </InputLabel>
                        <TextField
                          id="cisName"
                          {...register('cisName')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約名義（かな）
                        </InputLabel>
                        <TextField
                          id="cisNameKana"
                          {...register('cisNameKana')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          需要場所郵便番号
                        </InputLabel>
                        <TextField
                          id="cisZip"
                          {...register('cisZip')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約電話番号
                        </InputLabel>
                        <TextField
                          id="cisPhoneNo"
                          {...register('cisPhoneNo')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    電気支払情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        電気契約支払区分
                      </InputLabel>
                      <CommonSelect
                        id="payment"
                        {...register('payment')}
                        control={control}
                        errors={errors}
                        master={paymentTypeConfig.entry}
                      />
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        CIS支払区分
                      </InputLabel>
                      <CommonSelect
                        id="paymentTypeRegitCis"
                        control={control}
                        errors={errors}
                        master={paymentTypeConfig.cis}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      {/* {getValues('payment') !==
                      paymentTypeConfig.entry.unknown ? (
                        <> */}
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        電気料金支払番号
                      </InputLabel>
                      <TextField
                        id="paymentNumber"
                        {...register('paymentNumber')}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                      {/* </>
                      ) : (
                        <></>
                      )} */}
                    </Grid2>
                    {/* <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        CIS支払情報 本人確認結果
                      </InputLabel>
                      <CommonSelect
                        id="identityCheckResult"
                        control={control}
                        errors={errors}
                        master={{
                          '1': 'Yes',
                          '2': 'No'
                        }}
                        defaultCd={'1'}
                      />
                    </Grid2> */}
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    CIS電気契約詳細情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        サービス契約番号
                      </InputLabel>
                      <TextField
                        id="serviceNo"
                        {...register('serviceNo')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        SM自動検針対象
                      </InputLabel>
                      <CommonSelect
                        id="smartMeterFlg"
                        control={control}
                        errors={errors}
                        master={{ true: '○', false: '×' }}
                        defaultCd={getValues('')}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        供給地点特定番号
                      </InputLabel>
                      <TextField
                        id="supplyPoint"
                        {...register('supplyPoint')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        再エネ買取
                      </InputLabel>
                      <CommonSelect
                        id="renewableEnergyFlg"
                        {...register('renewableEnergyFlg')}
                        control={control}
                        errors={errors}
                        master={{ true: '○', false: '×' }}
                        defaultCd={getValues('renewableEnergyFlg')}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        契約種別コード
                      </InputLabel>
                      <TextField
                        id="contractTypeCd"
                        {...register('contractTypeCd')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        高圧
                      </InputLabel>
                      <CommonSelect
                        id="highVoltageFlg"
                        {...register('highVoltageFlg')}
                        control={control}
                        errors={errors}
                        master={{ true: '○', false: '×' }}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        料金プラン区分コード
                      </InputLabel>
                      <TextField
                        id="planTypeCd"
                        {...register('planTypeCd')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        マスタ化済
                      </InputLabel>
                      <CommonSelect
                        id="masteringCompleteFlg"
                        {...register('masteringCompleteFlg')}
                        control={control}
                        errors={errors}
                        master={{ true: '○', false: '×' }}
                        defaultCd={getValues('masteringCompleteFlg')}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        契約容量
                      </InputLabel>
                      <TextField
                        id="contractCapacity"
                        {...register('contractCapacity')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        需要開始年月日
                      </InputLabel>
                      <TextField
                        id="demandStartDate"
                        {...register('demandStartDate')}
                        variant="standard"
                        margin="dense"
                        type="date"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    その他管理情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        GMO会員番号
                      </InputLabel>
                      <TextField
                        id="memberId"
                        defaultValue={getValues('memberId')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        PaymentアカウントID
                      </InputLabel>
                      <TextField
                        id="zuoraAccountId"
                        {...register('zuoraAccountId')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        TCID識別子
                      </InputLabel>
                      <TextField
                        id="tcId"
                        {...register('tcId')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        サブスクリプション番号
                      </InputLabel>
                      <TextField
                        id="zuoraSubscriptionId"
                        {...register('zuoraSubscriptionId')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    備考
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 12 }}>
                      <TextField
                        id="notes"
                        {...register('notes')}
                        // defaultValue={remarks}
                        multiline
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '100%' }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
              </>
            ) : (
              <>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    申込管理情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込日
                      </InputLabel>
                      <TextField
                        id="applicationDate"
                        // defaultValue={applicationDate}
                        {...register('applicationDate')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    {/* <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込不備内容
                      </InputLabel>
                      <CommonSelect
                        id="incompleteCd"
                        control={control}
                        errors={errors}
                        {...register('incompleteCd')}
                        master={incompleteConfig}
                      />
                    </Grid2> */}
                  </Grid2>
                  <Grid2 container mt={2}>
                    {/* <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込パターン
                      </InputLabel>
                      <CommonSelect
                        id="pattern"
                        control={control}
                        errors={errors}
                        {...register('pattern')}
                        master={entryPatternConfig}
                        // defaultCd={pattern}
                      />
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込不備内容詳細
                      </InputLabel>
                      <TextField
                        id="incompleteDetail"
                        {...register('incompleteDetail')}
                        // defaultValue={incompleteDetail}
                        multiline
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '50%' }}></TextField>
                    </Grid2> */}
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    契約管理情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        契約日
                      </InputLabel>
                      <TextField
                        id="serviceStartDate"
                        {...register('serviceStartDate')}
                        // defaultValue={serviceStartDate}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        CIS廃止速報情報
                      </InputLabel>
                      <TextField
                        id="cancellationInfo"
                        defaultValue={
                          !isEmpty(getValues('scheduledAbolitionDate'))
                            ? 'あり'
                            : 'なし'
                        }
                        required
                        variant="standard"
                        disabled
                        margin="dense"
                        sx={{ minWidth: '50%' }}></TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        CIS廃止予定日
                      </InputLabel>
                      <TextField
                        id="scheduledAbolitionDate"
                        {...register('scheduledAbolitionDate')}
                        type={'date'}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        解約（予定）日
                      </InputLabel>
                      <TextField
                        id="cancellationDate"
                        {...register('cancellationDate')}
                        // defaultValue={cancellationDate}
                        type={'date'}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    契約者情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        氏名（漢字）
                      </InputLabel>
                      <TextField
                        id="lastName"
                        {...register('lastName')}
                        error={'lastName' in errors}
                        helperText={errors.lastName?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '25%',
                          textAlign: 'center'
                        }}></TextField>
                      <TextField
                        id="firstName"
                        {...register('firstName')}
                        error={'firstName' in errors}
                        helperText={errors.firstName?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '25%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        TCIDメールアドレス
                      </InputLabel>
                      <TextField
                        id="mail"
                        {...register('mail')}
                        error={'mail' in errors}
                        helperText={errors.mail?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        氏名（かな）
                      </InputLabel>
                      <TextField
                        id="lastNameHiragana"
                        {...register('lastNameHiragana')}
                        error={'lastNameHiragana' in errors}
                        helperText={errors.lastNameHiragana?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '25%',
                          textAlign: 'center'
                        }}></TextField>
                      <TextField
                        id="firstNameHiragana"
                        {...register('firstNameHiragana')}
                        error={'firstNameHiragana' in errors}
                        helperText={errors.firstNameHiragana?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '25%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        郵便番号
                      </InputLabel>
                      <TextField
                        id="zip"
                        {...register('zip')}
                        error={'zip' in errors}
                        helperText={errors.zip?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        電話番号
                      </InputLabel>
                      <TextField
                        id="phoneNo"
                        {...register('phoneNo')}
                        error={'phoneNo' in errors}
                        helperText={errors.phoneNo?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        住所•建物名等
                      </InputLabel>
                      <TextField
                        id="state"
                        {...register('state')}
                        error={'state' in errors}
                        helperText={errors.state?.message as string}
                        // defaultValue={address}
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '10%' }}></TextField>
                      <TextField
                        id="city"
                        {...register('city')}
                        error={'city' in errors}
                        helperText={errors.city?.message as string}
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '10%' }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'end'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 12 }}></Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'end'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel
                        sx={{
                          minWidth: '40%',
                          textAlign: 'center'
                        }}></InputLabel>
                      <TextField
                        id="address"
                        {...register('address')}
                        error={'address' in errors}
                        helperText={errors.address?.message as string}
                        // defaultValue={address}
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '50%' }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    契約プラン情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        紹介コード
                      </InputLabel>
                      <TextField
                        id="referralCd"
                        {...register('referralCd')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        料金プラン
                      </InputLabel>
                      <TextField
                        id="paymentPlan0"
                        defaultValue={
                          !isNil(getValues('paymentPlan0'))
                            ? getValues('paymentPlan0')
                            : '取得できませんでした'
                        }
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2} justifyContent={'end'}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        料金プラン（割引1）
                      </InputLabel>
                      <TextField
                        id="paymentPlan1"
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}>
                        {!isNil(getValues('paymentPlan1'))
                          ? getValues('paymentPlan1')
                          : ''}
                      </TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2} justifyContent={'end'}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        料金プラン（割引2）
                      </InputLabel>
                      <TextField
                        id="paymentPlan2"
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}>
                        {!isNil(getValues('paymentPlan2'))
                          ? getValues('paymentPlan2')
                          : ''}
                      </TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    被見守者情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        お客さま番号
                      </InputLabel>
                      <TextField
                        id="customerId"
                        {...register('customerId')}
                        variant="standard"
                        margin="dense"
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}
                        disabled></TextField>
                    </Grid2>
                    {/* <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        お客さま番号入力状況
                      </InputLabel>
                      <CommonSelect
                        id="customerStatus"
                        control={control}
                        errors={errors}
                        master={customuerStatusConfig}
                        // defaultCd={'1'}
                      />
                    </Grid2> */}
                  </Grid2>
                  <Grid2 container mt={2} size={{ xs: 12, lg: 6 }}>
                    {/* <Grid2
                      container
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <Typography
                        bgcolor={'#EEE'}
                        variant="subtitle1"
                        align="center"
                        sx={{ width: '100%' }}>
                        お客さま入力情報
                      </Typography>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約名義（漢字）
                        </InputLabel>
                        <TextField
                          id="nameWrittenCustomer"
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約名義（かな）
                        </InputLabel>
                        <TextField
                          id="nameKanaWrittenCustomer"
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          需要場所郵便番号
                        </InputLabel>
                        <TextField
                          id="zipCodeWrittenCustomer"
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約電話番号
                        </InputLabel>
                        <TextField
                          id="phoneWrittenCustomer"
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                    </Grid2> */}
                    <Grid2
                      container
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 12 }}>
                      <Typography
                        bgcolor={'#EEE'}
                        variant="subtitle1"
                        align="center"
                        sx={{ width: '100%' }}>
                        CIS登録情報
                      </Typography>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約名義（漢字）
                        </InputLabel>
                        <TextField
                          id="cisName"
                          {...register('cisName')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約名義（かな）
                        </InputLabel>
                        <TextField
                          id="cisNameKana"
                          {...register('cisNameKana')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          需要場所郵便番号
                        </InputLabel>
                        <TextField
                          id="cisZip"
                          {...register('cisZip')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                      <Grid2
                        display={'flex'}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                        size={{ xs: 12 }}>
                        <InputLabel
                          sx={{ minWidth: '40%', textAlign: 'center' }}>
                          電気契約電話番号
                        </InputLabel>
                        <TextField
                          id="cisPhoneNo"
                          {...register('cisPhoneNo')}
                          variant="standard"
                          margin="dense"
                          disabled
                          sx={{
                            minWidth: '50%',
                            textAlign: 'center'
                          }}></TextField>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    電気支払情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    {/* <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        電気契約支払区分
                      </InputLabel>
                      <CommonSelect
                        id="paymentType"
                        {...register('paymentType')}
                        control={control}
                        errors={errors}
                        master={paymentTypeConfig.entry}
                        defaultCd={'01'}
                        disabled
                      />
                    </Grid2> */}
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        CIS支払区分
                      </InputLabel>
                      <CommonSelect
                        id="cisPaymentTypeCd"
                        control={control}
                        errors={errors}
                        master={paymentTypeConfig.cis}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    {/* <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        電気料金支払カード番号
                      </InputLabel>
                      <TextField
                        id="creditNumber"
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2> */}
                    {/* <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        CIS支払情報 本人確認結果
                      </InputLabel>
                      <CommonSelect
                        id="identityCheckResult"
                        control={control}
                        errors={errors}
                        master={{
                          '1': 'Yes',
                          '2': 'No'
                        }}
                        defaultCd={'1'}
                        disabled
                      />
                    </Grid2> */}
                  </Grid2>
                  {/* <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        電気料金支払口座番号
                      </InputLabel>
                      <TextField
                        id="transferNumber"
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2> */}
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    CIS電気契約詳細情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        サービス契約番号
                      </InputLabel>
                      <TextField
                        id="serviceNo"
                        {...register('serviceNo')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        SM自動検針対象
                      </InputLabel>
                      <CommonSelect
                        id="smartMeterFlg"
                        control={control}
                        errors={errors}
                        master={{ true: '○' }}
                        defaultCd={'true'}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        供給地点特定番号
                      </InputLabel>
                      <TextField
                        id="supplyPoint"
                        {...register('supplyPoint')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        再エネ買取
                      </InputLabel>
                      <CommonSelect
                        id="renewableEnergyFlg"
                        {...register('renewableEnergyFlg')}
                        control={control}
                        errors={errors}
                        master={{ true: '○', false: '×' }}
                        defaultCd={getValues('renewableEnergyFlg')}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        契約種別コード
                      </InputLabel>
                      <TextField
                        id="contractTypeCd"
                        {...register('contractTypeCd')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        高圧
                      </InputLabel>
                      <CommonSelect
                        id="highVoltageFlg"
                        {...register('highVoltageFlg')}
                        control={control}
                        errors={errors}
                        master={{ true: '○', false: '×' }}
                        defaultCd={getValues('highVoltageFlg')}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        料金プラン区分コード
                      </InputLabel>
                      <TextField
                        id="planTypeCd"
                        {...register('planTypeCd')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        マスタ化済
                      </InputLabel>
                      <CommonSelect
                        id="masteringCompleteFlg"
                        {...register('masteringCompleteFlg')}
                        control={control}
                        errors={errors}
                        master={{ true: '○', false: '×' }}
                        defaultCd={getValues('masteringCompleteFlg')}
                        disabled
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        契約容量
                      </InputLabel>
                      <TextField
                        id="contractCapacity"
                        {...register('contractCapacity')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        需要開始年月日
                      </InputLabel>
                      <TextField
                        id="demandStartDate"
                        {...register('demandStartDate')}
                        variant="standard"
                        margin="dense"
                        type="date"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
                {/* <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    申込時カテエネ登録情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込時カテエネ会員番号
                      </InputLabel>
                      <TextField
                        id="kateeneId"
                        {...register('kateeneId')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        申込時カテエネ登録状態
                      </InputLabel>
                      <CommonSelect
                        id="kateneRegistStatus"
                        control={control}
                        errors={errors}
                        master={kateneStatusConfig}
                        defaultCd={'1'}
                      />
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        性別
                      </InputLabel>
                      <CommonSelect
                        id="gender"
                        control={control}
                        errors={errors}
                        master={{
                          male: '男性',
                          female: '女性',
                          unknown: '分からない'
                        }}
                        defaultCd={'male'}
                      />
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        生年月日
                      </InputLabel>
                      <TextField
                        id="birthDay"
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper> */}
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    その他管理情報
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        GMO会員番号
                      </InputLabel>
                      <TextField
                        id="memberId"
                        defaultValue={getValues('memberId')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        PaymentアカウントID
                      </InputLabel>
                      <TextField
                        id="zuoraAccountId"
                        {...register('zuoraAccountId')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        TCID識別子
                      </InputLabel>
                      <TextField
                        id="tcid"
                        {...register('tcid')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 6 }}>
                      <InputLabel sx={{ minWidth: '40%', textAlign: 'center' }}>
                        サブスクリプション番号
                      </InputLabel>
                      <TextField
                        id="zuoraSubscriptionId"
                        {...register('zuoraSubscriptionId')}
                        variant="standard"
                        margin="dense"
                        disabled
                        sx={{
                          minWidth: '50%',
                          textAlign: 'center'
                        }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
                <Paper elevation={3} sx={{ mt: 3, padding: 2 }}>
                  <Typography
                    variant="h6"
                    padding={1}
                    width={'100%'}
                    textAlign={'center'}>
                    備考
                  </Typography>
                  <Divider />
                  <Grid2 container mt={2}>
                    <Grid2
                      display={'flex'}
                      justifyContent={'space-around'}
                      alignItems={'center'}
                      size={{ xs: 12, lg: 12 }}>
                      <TextField
                        id="notes"
                        {...register('notes')}
                        // defaultValue={remarks}
                        multiline
                        variant="standard"
                        margin="dense"
                        sx={{ minWidth: '100%' }}></TextField>
                    </Grid2>
                  </Grid2>
                </Paper>
              </>
            )}
          </Box>
          <Grid2>
            <Box
              display={'flex'}
              justifyContent={'space-around'}
              paddingTop={3}>
              <Box minWidth={'40%'}>
                <Button
                  variant="contained"
                  color="inherit"
                  onClick={handleClickBack}
                  fullWidth>
                  戻る
                </Button>
              </Box>
              <Box minWidth={'40%'}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit(onSubmit)}
                  fullWidth>
                  登録
                </Button>
              </Box>
            </Box>
          </Grid2>
        </Container>
      </FormProvider>
      <Snackbar
        open={alertStatus}
        autoHideDuration={5000}
        onClose={handleAlertClose}>
        <Alert severity="error">
          <AlertTitle>{alertStatus ? '更新処理に失敗しました' : ''}</AlertTitle>
          {alertStatus ? 'システム担当にご連絡ください。' : ''}
        </Alert>
      </Snackbar>
    </>
  )
}
